import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Switch } from "../components/ui/switch";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { useAuth, authFetch } from "../context/AuthContext";
import { User, Bell, Loader2, DollarSign, Smartphone, FileText, ChevronRight, Crown, Sparkles } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function Settings() {
  const { user, updateUser } = useAuth();
  const [insightFrequency, setInsightFrequency] = useState(user?.insight_frequency || "monthly");
  const [notificationPrefs, setNotificationPrefs] = useState({
    enabled: true,
    frequency: "weekly",
    roi_threshold_value: 150,
    roi_threshold_days: 60,
  });
  const [saving, setSaving] = useState(false);
  
  // Push notification state
  const [pushStatus, setPushStatus] = useState({
    enabled: false,
    available: false,
    browserPermission: "default",
    loading: true
  });
  const [pushToggling, setPushToggling] = useState(false);

  // Check push notification status
  const checkPushStatus = useCallback(async () => {
    try {
      // Check browser support
      const supported = "serviceWorker" in navigator && "PushManager" in window;
      const permission = supported ? Notification.permission : "denied";
      
      // Check backend status
      const response = await authFetch(`${API_URL}/api/push/status`, {
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setPushStatus({
          enabled: data.enabled && permission === "granted",
          available: supported && data.available,
          browserPermission: permission,
          loading: false
        });
      } else {
        setPushStatus(prev => ({ ...prev, loading: false, available: supported }));
      }
    } catch (error) {
      console.error("Push status check failed:", error);
      setPushStatus(prev => ({ ...prev, loading: false }));
    }
  }, []);

  // Subscribe to push notifications
  const subscribeToPush = async () => {
    setPushToggling(true);
    try {
      // Request notification permission
      const permission = await Notification.requestPermission();
      
      if (permission !== "granted") {
        toast.error("Notification permission denied");
        setPushStatus(prev => ({ ...prev, browserPermission: permission }));
        return;
      }

      // Get VAPID public key
      const vapidResponse = await authFetch(`${API_URL}/api/push/vapid-key`, {
        
      });
      
      if (!vapidResponse.ok) throw new Error("Failed to get VAPID key");
      const { publicKey } = await vapidResponse.json();

      // Register service worker if not already
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey)
      });

      // Send subscription to backend
      const subResponse = await authFetch(`${API_URL}/api/push/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          endpoint: subscription.endpoint,
          keys: {
            p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
            auth: arrayBufferToBase64(subscription.getKey("auth"))
          }
        })
      });

      if (subResponse.ok) {
        setPushStatus(prev => ({ ...prev, enabled: true, browserPermission: "granted" }));
        updateUser({ ...user, push_enabled: true });
        toast.success("Push notifications enabled");
      } else {
        throw new Error("Failed to save subscription");
      }
    } catch (error) {
      console.error("Push subscription error:", error);
      toast.error("Failed to enable notifications");
    } finally {
      setPushToggling(false);
    }
  };

  // Unsubscribe from push notifications
  const unsubscribeFromPush = async () => {
    setPushToggling(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Unsubscribe from backend
        await authFetch(`${API_URL}/api/push/unsubscribe`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: {
              p256dh: arrayBufferToBase64(subscription.getKey("p256dh")),
              auth: arrayBufferToBase64(subscription.getKey("auth"))
            }
          })
        });

        // Unsubscribe from browser
        await subscription.unsubscribe();
      }

      setPushStatus(prev => ({ ...prev, enabled: false }));
      updateUser({ ...user, push_enabled: false });
      toast.success("Push notifications disabled");
    } catch (error) {
      console.error("Push unsubscribe error:", error);
      toast.error("Failed to disable notifications");
    } finally {
      setPushToggling(false);
    }
  };

  // Handle push toggle
  const handlePushToggle = async (enabled) => {
    if (enabled) {
      await subscribeToPush();
    } else {
      await unsubscribeFromPush();
    }
  };

  // Send test notification
  const sendTestNotification = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/push/test`, {
        method: "POST",
        
      });
      
      if (response.ok) {
        toast.success("Test notification sent");
      } else {
        const data = await response.json();
        toast.error(data.detail || "Failed to send test");
      }
    } catch (error) {
      toast.error("Failed to send test notification");
    }
  };

  useEffect(() => {
    checkPushStatus();
  }, [checkPushStatus]);

  useEffect(() => {
    if (user?.insight_frequency) {
      setInsightFrequency(user.insight_frequency);
    }
    if (user?.notification_preferences) {
      setNotificationPrefs({
        enabled: user.notification_preferences.enabled ?? true,
        frequency: user.notification_preferences.frequency || "weekly",
        roi_threshold_value: user.notification_preferences.roi_threshold_value || 150,
        roi_threshold_days: user.notification_preferences.roi_threshold_days || 60,
      });
    }
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API_URL}/api/user/profile`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify({
          insight_frequency: insightFrequency,
          notification_preferences: notificationPrefs,
        }),
      });

      if (response.ok) {
        const updatedUser = await response.json();
        updateUser(updatedUser);
        toast.success("Settings saved");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-8 max-w-2xl" data-testid="settings-page">
        {/* Page Header */}
        <div>
          <h1 className="font-heading text-3xl md:text-4xl tracking-tight text-primary mb-2">
            Settings
          </h1>
          <p className="text-muted-foreground">
            Manage your account preferences
          </p>
        </div>

        {/* Profile Card */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
              <User className="w-5 h-5" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            <div className="flex items-center gap-4">
              {user?.picture && (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-16 h-16 rounded-full object-cover"
                />
              )}
              <div>
                <p className="font-medium text-lg">{user?.name}</p>
                <p className="text-muted-foreground">{user?.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Membership Status Card */}
        <Card className="border border-border shadow-none" data-testid="membership-status-card">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
              <Crown className="w-5 h-5" />
              Membership
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-lg ${
                  user?.subscription?.tier === 'pro' 
                    ? 'bg-purple-100 text-purple-700' 
                    : user?.subscription?.tier === 'plus' 
                      ? 'bg-blue-100 text-blue-700' 
                      : 'bg-gray-100 text-gray-600'
                }`}>
                  {user?.subscription?.tier === 'pro' ? (
                    <Sparkles className="w-6 h-6" />
                  ) : user?.subscription?.tier === 'plus' ? (
                    <Crown className="w-6 h-6" />
                  ) : (
                    <User className="w-6 h-6" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-lg">
                    {user?.subscription?.tier === 'pro' 
                      ? 'WornWell Pro' 
                      : user?.subscription?.tier === 'plus' 
                        ? 'WornWell Plus' 
                        : 'Starter (Free)'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {user?.subscription?.status === 'active' && user?.subscription?.tier !== 'free' ? (
                      <>Active • {user?.subscription?.billing_cycle === 'yearly' ? 'Annual' : 'Monthly'} billing</>
                    ) : user?.subscription?.status === 'canceling' ? (
                      <>Canceling at period end</>
                    ) : (
                      <>Limited features</>
                    )}
                  </p>
                  {user?.subscription?.current_period_end && user?.subscription?.tier !== 'free' && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {user?.subscription?.status === 'canceling' ? 'Access until' : 'Renews'}: {new Date(user.subscription.current_period_end).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div>
                {(!user?.subscription?.tier || user?.subscription?.tier === 'free') ? (
                  <Link to="/subscription">
                    <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm" data-testid="upgrade-btn">
                      Upgrade
                    </Button>
                  </Link>
                ) : user?.subscription?.tier === 'plus' ? (
                  <Link to="/subscription">
                    <Button variant="outline" className="rounded-sm" data-testid="upgrade-to-pro-btn">
                      Upgrade to Pro
                    </Button>
                  </Link>
                ) : (
                  <Link to="/subscription">
                    <Button variant="outline" className="rounded-sm" data-testid="manage-subscription-btn">
                      Manage Plan
                    </Button>
                  </Link>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Insights Preferences Card */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
              <Bell className="w-5 h-5" />
              Insights Frequency
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 pt-4 space-y-6">
            <div className="space-y-2">
              <Label htmlFor="insight-frequency">How often would you like insights?</Label>
              <Select value={insightFrequency} onValueChange={setInsightFrequency}>
                <SelectTrigger className="w-full max-w-xs rounded-sm" data-testid="insight-frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="seasonal">Seasonal (Quarterly)</SelectItem>
                </SelectContent>
              </Select>
              <div className="mt-3 p-3 bg-sand-50 rounded-sm text-sm text-muted-foreground">
                {insightFrequency === "weekly" && (
                  <p>You'll see insights based on the last 7 days of activity.</p>
                )}
                {insightFrequency === "monthly" && (
                  <p>You'll see insights based on the last 30 days of activity.</p>
                )}
                {insightFrequency === "seasonal" && (
                  <p>You'll see insights based on the last 90 days (Fall/Winter or Spring/Summer).</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Push Notifications Card */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Push Notifications
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Get timely reminders on your device
            </p>
          </CardHeader>
          <CardContent className="p-6 pt-4 space-y-4">
            {pushStatus.loading ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="w-4 h-4 animate-spin" />
                Checking notification status...
              </div>
            ) : !pushStatus.available ? (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800">
                Push notifications are not supported in this browser.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div>
                    <Label htmlFor="push-enabled">Enable Push Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Streak reminders & sell opportunities
                    </p>
                  </div>
                  <Switch
                    id="push-enabled"
                    checked={pushStatus.enabled}
                    onCheckedChange={handlePushToggle}
                    disabled={pushToggling}
                    data-testid="push-notifications-switch"
                  />
                </div>

                {pushStatus.browserPermission === "denied" && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm text-sm text-amber-800">
                    Notifications are blocked. Please enable them in your browser settings.
                  </div>
                )}

                {pushStatus.enabled && (
                  <div className="pt-2 border-t border-border">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={sendTestNotification}
                      className="text-xs"
                      data-testid="test-notification-btn"
                    >
                      Send Test Notification
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      You'll receive reminders for daily outfit logging and high-value sell opportunities.
                    </p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Notification Preferences Card */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Sell Opportunity Notifications
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Get notified about high-value items you might want to sell
            </p>
          </CardHeader>
          <CardContent className="p-6 pt-4 space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div>
                <Label htmlFor="notifications-enabled">Enable Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  Receive alerts for sell opportunities
                </p>
              </div>
              <Switch
                id="notifications-enabled"
                checked={notificationPrefs.enabled}
                onCheckedChange={(checked) =>
                  setNotificationPrefs({ ...notificationPrefs, enabled: checked })
                }
                data-testid="notifications-enabled-switch"
              />
            </div>

            {notificationPrefs.enabled && (
              <>
                {/* Notification Frequency */}
                <div className="space-y-2">
                  <Label htmlFor="notification-frequency">Notification Frequency</Label>
                  <Select
                    value={notificationPrefs.frequency}
                    onValueChange={(value) =>
                      setNotificationPrefs({ ...notificationPrefs, frequency: value })
                    }
                  >
                    <SelectTrigger className="w-full max-w-xs rounded-sm" data-testid="notification-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="weekly">Weekly Digest</SelectItem>
                      <SelectItem value="monthly">Monthly Digest</SelectItem>
                      <SelectItem value="seasonal">Seasonal Digest</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    {notificationPrefs.frequency === "weekly" && "You'll receive a summary every week."}
                    {notificationPrefs.frequency === "monthly" && "You'll receive a summary once a month."}
                    {notificationPrefs.frequency === "seasonal" && "You'll receive a summary each season (Fall/Winter, Spring/Summer)."}
                  </p>
                </div>

                {/* ROI Thresholds */}
                <div className="space-y-4 pt-4 border-t border-border">
                  <p className="text-sm font-medium">Alert me when an item:</p>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="threshold-value">Has value over</Label>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">$</span>
                        <Input
                          id="threshold-value"
                          type="number"
                          min="0"
                          value={notificationPrefs.roi_threshold_value}
                          onChange={(e) =>
                            setNotificationPrefs({
                              ...notificationPrefs,
                              roi_threshold_value: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-24"
                          data-testid="threshold-value-input"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="threshold-days">Unworn for over</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          id="threshold-days"
                          type="number"
                          min="0"
                          value={notificationPrefs.roi_threshold_days}
                          onChange={(e) =>
                            setNotificationPrefs({
                              ...notificationPrefs,
                              roi_threshold_days: parseInt(e.target.value) || 0,
                            })
                          }
                          className="w-24"
                          data-testid="threshold-days-input"
                        />
                        <span className="text-muted-foreground">days</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-sand-50 rounded-sm text-sm text-muted-foreground">
                    <p>
                      You'll be notified about items worth <strong>${notificationPrefs.roi_threshold_value}+</strong> that 
                      you haven't worn in <strong>{notificationPrefs.roi_threshold_days}+ days</strong>.
                    </p>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Legal Section */}
        <Card className="border border-border shadow-none">
          <CardHeader className="p-6 pb-2">
            <CardTitle className="font-heading text-xl font-medium flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Legal
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              Privacy policy, terms, and legal information
            </p>
          </CardHeader>
          <CardContent className="p-6 pt-4">
            <Link
              to="/legal"
              className="flex items-center justify-between p-3 rounded-lg hover:bg-sand-50 transition-colors -mx-3"
              data-testid="legal-link"
            >
              <span className="font-medium">View Legal Documents</span>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </Link>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-sm h-12 px-8 font-medium tracking-wide text-sm uppercase"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            "Save All Changes"
          )}
        </Button>
      </div>
    </Layout>
  );
}

// Helper: Convert VAPID key from base64 URL to Uint8Array
function urlBase64ToUint8Array(base64String) {
  // Handle both hex and base64 formats
  if (/^[0-9a-fA-F]+$/.test(base64String)) {
    // Hex format
    const bytes = new Uint8Array(base64String.length / 2);
    for (let i = 0; i < bytes.length; i++) {
      bytes[i] = parseInt(base64String.substr(i * 2, 2), 16);
    }
    return bytes;
  }
  
  // Base64 URL format
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

// Helper: Convert ArrayBuffer to base64
function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  bytes.forEach((b) => (binary += String.fromCharCode(b)));
  return window.btoa(binary);
}
